import React, { useMemo, useRef, useState } from "react";
import styled from "styled-components";
import firebase from "../../../database/firebase";
import DB_PATH from "../../../database/DB_PATH";
import { Button } from "../../../design-system";
import useReelCharacters from "../../../database/reelCharacters/useReelCharacters";
import getAllAuthors from "../../../database/authors/getAllAuthors";
import { ReelCharacter } from "../../../types/ReelCharacter";
import { createOpenAIChatCompletion } from "../../../services/openaiFunctions";
import {
  generateGeminiImage,
  generateElevenLabsVoice,
} from "../plan/dayplan/components/dailyStep/ViralReelCreator.api";
import { uploadMedia } from "../../../database/media/cloudflareMedia";

// Cast library for viral reels and mini movies. Characters created here are
// reusable across reels, series and the viral-reel-studio Cowork skill. The
// same records are exposed over MCP (list_reel_characters / save_reel_character).

type CharacterStudioProps = {
  communityId?: string;
  geminiApiKey: string;
  elevenLabsApiKey?: string;
};

type DraftFields = {
  name: string;
  role: string;
  brief: string;
  visualDescription: string;
  personality: string;
  backstory: string;
  speakingStyle: string;
  voiceId: string;
  voiceName: string;
};

const EMPTY_DRAFT: DraftFields = {
  name: "",
  role: "",
  brief: "",
  visualDescription: "",
  personality: "",
  backstory: "",
  speakingStyle: "",
  voiceId: "",
  voiceName: "",
};

const VOICE_PREVIEW_TEXT =
  "You found me. Now let me tell you a story you will not forget.";

// ─── Styles ─────────────────────────────────────────────────────────────────

const Root = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 420px);
  gap: var(--space-4);
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const ListPane = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
  flex-wrap: wrap;
`;

const Title = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const Subtitle = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  max-width: 64ch;
  line-height: var(--line-height-base);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: var(--space-3);
`;

const Card = styled.button<{ $active: boolean; $archived: boolean }>`
  appearance: none;
  text-align: left;
  padding: 0;
  border-radius: var(--radius-lg);
  overflow: hidden;
  cursor: pointer;
  background: var(--color-surface);
  border: 1.5px solid
    ${(p) => (p.$active ? "var(--color-accent)" : "var(--color-border-subtle)")};
  opacity: ${(p) => (p.$archived ? 0.55 : 1)};
  transition: border-color var(--duration-fast) var(--ease-standard);

  &:hover {
    border-color: var(--color-border-strong);
  }
`;

const CardImage = styled.div<{ $src?: string }>`
  width: 100%;
  aspect-ratio: 3 / 4;
  background: ${(p) =>
    p.$src
      ? `url(${p.$src}) center / cover no-repeat`
      : "var(--color-surface-subtle)"};
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-tertiary);
  font-size: 28px;
`;

const CardBody = styled.div`
  padding: var(--space-2) var(--space-3) var(--space-3);
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CardName = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardMeta = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const VoiceBadge = styled.span<{ $hasVoice: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  align-self: flex-start;
  padding: 1px 8px;
  border-radius: var(--radius-pill);
  font-family: var(--font-sans);
  font-size: 10px;
  font-weight: var(--font-weight-medium);
  background: ${(p) =>
    p.$hasVoice ? "var(--color-accent-subtle)" : "var(--color-surface-subtle)"};
  color: ${(p) =>
    p.$hasVoice ? "var(--color-accent-text)" : "var(--color-text-tertiary)"};
`;

const EditorPane = styled.div`
  position: sticky;
  top: var(--space-3);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
`;

const EditorTitle = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
`;

const Input = styled.input`
  height: 32px;
  padding: 0 var(--space-2);
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface-subtle);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);

  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const TextArea = styled.textarea`
  min-height: 64px;
  padding: var(--space-2);
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface-subtle);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  line-height: var(--line-height-base);
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const Select = styled.select`
  height: 32px;
  padding: 0 var(--space-2);
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface-subtle);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: var(--space-2);
  flex-wrap: wrap;
  align-items: center;
`;

const RefPreview = styled.div<{ $src?: string }>`
  width: 120px;
  aspect-ratio: 3 / 4;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-subtle);
  background: ${(p) =>
    p.$src
      ? `url(${p.$src}) center / cover no-repeat`
      : "var(--color-surface-subtle)"};
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-tertiary);
  font-size: 20px;
`;

const StatusMsg = styled.div<{ $error?: boolean }>`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: ${(p) => (p.$error ? "var(--color-danger)" : "var(--color-text-secondary)")};
`;

const EmptyState = styled.div`
  padding: var(--space-6);
  border-radius: var(--radius-lg);
  border: 1.5px dashed var(--color-border-subtle);
  background: var(--color-surface-subtle);
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  text-align: center;
`;

// ─── Helpers ────────────────────────────────────────────────────────────────

const parseChatJson = (data: any): Record<string, string> => {
  const content: string = data?.choices?.[0]?.message?.content || "";
  const cleaned = content
    .replace(/^```(?:json)?/m, "")
    .replace(/```\s*$/m, "")
    .trim();
  const start = cleaned.indexOf("{");
  const end = cleaned.lastIndexOf("}");
  if (start === -1 || end === -1) throw new Error("No JSON in AI response");
  return JSON.parse(cleaned.slice(start, end + 1));
};

const dataUrlToBlob = async (dataUrl: string): Promise<Blob> => {
  const res = await fetch(dataUrl);
  return res.blob();
};

const buildReferencePrompt = (visualDescription: string, role?: string): string =>
  `Cinematic 9:16 portrait establishing shot of ${visualDescription}. ` +
  `${role ? `They read as the story's ${role}. ` : ""}` +
  `The character is the clear subject: confident posture, facing the lens, expressive but composed. ` +
  `Specific, atmospheric, visually iconic environment native to who they are. ` +
  `Warm directional lighting that flatters the subject. No text, no watermarks, no logos in the image.`;

// ─── Component ──────────────────────────────────────────────────────────────

const CharacterStudio: React.FC<CharacterStudioProps> = ({
  communityId,
  geminiApiKey,
  elevenLabsApiKey,
}) => {
  const cid = communityId || "";
  const {
    characters,
    isLoading,
    saveCharacter,
    archiveCharacter,
    deleteCharacter,
  } = useReelCharacters({ communityId: cid });
  const authors = getAllAuthors({ communityId: cid });
  const voiceAuthors = useMemo(
    () => authors.filter((a) => !!a.voiceId),
    [authors]
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftFields>(EMPTY_DRAFT);
  const [showArchived, setShowArchived] = useState(false);
  const [isDrafting, setIsDrafting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingRef, setIsGeneratingRef] = useState(false);
  const [isPreviewingVoice, setIsPreviewingVoice] = useState(false);
  const [refPreview, setRefPreview] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [error, setError] = useState<string>("");
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);

  const visible = useMemo(
    () => characters.filter((c) => showArchived || c.status !== "archived"),
    [characters, showArchived]
  );
  const selected = useMemo(
    () => characters.find((c) => c.id === selectedId) || null,
    [characters, selectedId]
  );

  const set = (field: keyof DraftFields) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => setDraft((d) => ({ ...d, [field]: e.target.value }));

  const openCharacter = (c: ReelCharacter) => {
    setSelectedId(c.id);
    setRefPreview(c.referenceImageUrl || "");
    setStatus("");
    setError("");
    setDraft({
      name: c.name || "",
      role: c.role || "",
      brief: "",
      visualDescription: c.visualDescription || "",
      personality: c.personality || "",
      backstory: c.backstory || "",
      speakingStyle: c.speakingStyle || "",
      voiceId: c.voiceId || "",
      voiceName: c.voiceName || "",
    });
  };

  const openNew = () => {
    setSelectedId(null);
    setRefPreview("");
    setStatus("");
    setError("");
    setDraft(EMPTY_DRAFT);
  };

  const handleDraftWithAI = async () => {
    if (!draft.name && !draft.brief) {
      setError("Give the character a name or a short brief first.");
      return;
    }
    setIsDrafting(true);
    setError("");
    setStatus("Drafting character sheet with AI...");
    try {
      const data = await createOpenAIChatCompletion<any>({
        model: "gpt-5.4",
        messages: [
          {
            role: "system",
            content:
              "You design recurring characters for short-form AI mini movies (9:16 viral reels). " +
              "Given a name, role and brief, return STRICT JSON with keys: " +
              "visualDescription (ONE stable sentence for image prompts: age, look, wardrobe, defining features; no camera or style words), " +
              "personality (2-3 sentences), " +
              "backstory (2-3 sentences), " +
              "speakingStyle (1-2 sentences: tone, pacing, vocabulary, quirks for writing dialogue). " +
              "The visualDescription must stay identical across every scene, so avoid anything situational.",
          },
          {
            role: "user",
            content: `Name: ${draft.name || "(pick one)"}\nRole: ${
              draft.role || "(infer)"
            }\nBrief: ${draft.brief || "(none)"}`,
          },
        ],
      });
      const parsed = parseChatJson(data);
      setDraft((d) => ({
        ...d,
        visualDescription: parsed.visualDescription || d.visualDescription,
        personality: parsed.personality || d.personality,
        backstory: parsed.backstory || d.backstory,
        speakingStyle: parsed.speakingStyle || d.speakingStyle,
      }));
      setStatus("Character sheet drafted. Review and save.");
    } catch (e) {
      console.error("Character AI draft failed:", e);
      setError("AI drafting failed. Try again.");
      setStatus("");
    } finally {
      setIsDrafting(false);
    }
  };

  const persistDraft = async (): Promise<string> => {
    const id = await saveCharacter({
      id: selectedId || undefined,
      name: draft.name.trim(),
      role: draft.role.trim() || undefined,
      visualDescription: draft.visualDescription.trim(),
      personality: draft.personality.trim() || undefined,
      backstory: draft.backstory.trim() || undefined,
      speakingStyle: draft.speakingStyle.trim() || undefined,
      voiceId: draft.voiceId.trim() || undefined,
      voiceName: draft.voiceName.trim() || undefined,
    });
    setSelectedId(id);
    return id;
  };

  const handleSave = async () => {
    if (!draft.name.trim()) {
      setError("Name is required.");
      return;
    }
    if (!draft.visualDescription.trim()) {
      setError("Visual description is required. It keeps the character consistent across scenes.");
      return;
    }
    setIsSaving(true);
    setError("");
    try {
      await persistDraft();
      setStatus("Character saved.");
    } catch (e) {
      console.error("Character save failed:", e);
      setError("Save failed. Try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateReference = async () => {
    if (!draft.visualDescription.trim()) {
      setError("Write or draft the visual description first.");
      return;
    }
    setIsGeneratingRef(true);
    setError("");
    setStatus("Generating master reference image...");
    try {
      const characterId = await persistDraft();
      const dataUrl = await generateGeminiImage(
        geminiApiKey,
        buildReferencePrompt(draft.visualDescription.trim(), draft.role.trim())
      );
      setRefPreview(dataUrl);
      setStatus("Uploading reference image...");
      const blob = await dataUrlToBlob(dataUrl);
      const path = `${DB_PATH.COMMUNITY}/${cid}/${DB_PATH.REEL_CHARACTER}/${characterId}/reference-${Date.now()}.png`;
      const url = (
        await uploadMedia({
          body: blob,
          kind: "image",
          contentType: "image/png",
          fileName: "reference.png",
          firebasePath: path,
        })
      ).url;
      await saveCharacter({
        id: characterId,
        name: draft.name.trim(),
        referenceImageUrl: url,
      });
      setRefPreview(url);
      setStatus("Reference image saved.");
    } catch (e) {
      console.error("Reference generation failed:", e);
      setError("Reference image generation failed. Try again.");
      setStatus("");
    } finally {
      setIsGeneratingRef(false);
    }
  };

  const handlePreviewVoice = async () => {
    if (!draft.voiceId.trim() || !elevenLabsApiKey) {
      setError("Pick a voice first.");
      return;
    }
    setIsPreviewingVoice(true);
    setError("");
    try {
      const blob = await generateElevenLabsVoice(
        elevenLabsApiKey,
        draft.voiceId.trim(),
        VOICE_PREVIEW_TEXT
      );
      const url = URL.createObjectURL(blob);
      previewAudioRef.current?.pause();
      const audio = new Audio(url);
      previewAudioRef.current = audio;
      audio.onended = () => URL.revokeObjectURL(url);
      await audio.play();
    } catch (e) {
      console.error("Voice preview failed:", e);
      setError("Voice preview failed. Check the voice id.");
    } finally {
      setIsPreviewingVoice(false);
    }
  };

  const handleVoiceAuthorPick = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const author = voiceAuthors.find((a) => a.voiceId === e.target.value);
    setDraft((d) => ({
      ...d,
      voiceId: e.target.value,
      voiceName: author?.fullName || d.voiceName,
    }));
  };

  const handleArchiveToggle = async () => {
    if (!selected) return;
    await archiveCharacter(selected.id, selected.status !== "archived");
  };

  const handleDelete = async () => {
    if (!selected) return;
    const ok = window.confirm(
      `Delete ${selected.name}? Existing reels keep their embedded copy, but the character leaves the cast library. Consider Archive instead.`
    );
    if (!ok) return;
    await deleteCharacter(selected.id);
    openNew();
  };

  if (!cid) {
    return <EmptyState>Sign in to a community to manage characters.</EmptyState>;
  }

  return (
    <Root>
      <ListPane>
        <HeaderRow>
          <div>
            <Title>Characters</Title>
            <Subtitle>
              Reusable cast for viral reels and mini movies. Each character keeps
              the same face, personality and voice across scenes, reels and
              episodic series. Claude can also read and update this library
              through the MCP tools.
            </Subtitle>
          </div>
          <ButtonRow>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setShowArchived((v) => !v)}
            >
              {showArchived ? "Hide archived" : "Show archived"}
            </Button>
            <Button size="sm" onClick={openNew}>
              New character
            </Button>
          </ButtonRow>
        </HeaderRow>

        {isLoading ? (
          <EmptyState>Loading characters...</EmptyState>
        ) : visible.length === 0 ? (
          <EmptyState>
            No characters yet. Create your first recurring character and it
            becomes available to every reel and to the Cowork reel studio.
          </EmptyState>
        ) : (
          <Grid>
            {visible.map((c) => (
              <Card
                key={c.id}
                $active={c.id === selectedId}
                $archived={c.status === "archived"}
                onClick={() => openCharacter(c)}
              >
                <CardImage $src={c.referenceImageUrl}>
                  {!c.referenceImageUrl && "🎭"}
                </CardImage>
                <CardBody>
                  <CardName>{c.name}</CardName>
                  <CardMeta>{c.role || "No role set"}</CardMeta>
                  <VoiceBadge $hasVoice={!!c.voiceId}>
                    {c.voiceId ? `🔊 ${c.voiceName || "Voice set"}` : "No voice"}
                  </VoiceBadge>
                </CardBody>
              </Card>
            ))}
          </Grid>
        )}
      </ListPane>

      <EditorPane>
        <EditorTitle>
          {selected ? `Edit: ${selected.name}` : "New character"}
        </EditorTitle>

        <Field>
          <Label>Name</Label>
          <Input value={draft.name} onChange={set("name")} placeholder="Sofia Marchetti" />
        </Field>

        <Field>
          <Label>Role</Label>
          <Input
            value={draft.role}
            onChange={set("role")}
            placeholder="protagonist, mentor, rival, comic relief"
          />
        </Field>

        <Field>
          <Label>Brief for AI (optional)</Label>
          <TextArea
            value={draft.brief}
            onChange={set("brief")}
            placeholder="A 34 year old Italian pastry chef turned business coach. Warm but brutally honest."
          />
        </Field>

        <ButtonRow>
          <Button
            size="sm"
            variant="secondary"
            onClick={handleDraftWithAI}
            disabled={isDrafting}
          >
            {isDrafting ? "Drafting..." : "Draft sheet with AI"}
          </Button>
        </ButtonRow>

        <Field>
          <Label>Visual description (the anchor used in every image prompt)</Label>
          <TextArea
            value={draft.visualDescription}
            onChange={set("visualDescription")}
            placeholder="A 34 year old Italian woman with dark curly hair in a low bun, flour-dusted navy apron over a white chef coat, small gold hoop earrings, warm brown eyes"
          />
        </Field>

        <Field>
          <Label>Personality</Label>
          <TextArea value={draft.personality} onChange={set("personality")} />
        </Field>

        <Field>
          <Label>Backstory</Label>
          <TextArea value={draft.backstory} onChange={set("backstory")} />
        </Field>

        <Field>
          <Label>Speaking style (guides their dialogue)</Label>
          <TextArea value={draft.speakingStyle} onChange={set("speakingStyle")} />
        </Field>

        <Field>
          <Label>Voice</Label>
          <Select value={draft.voiceId} onChange={handleVoiceAuthorPick}>
            <option value="">No voice</option>
            {voiceAuthors.map((a) => (
              <option key={a.id} value={a.voiceId as string}>
                {a.fullName}
              </option>
            ))}
            {draft.voiceId &&
              !voiceAuthors.some((a) => a.voiceId === draft.voiceId) && (
                <option value={draft.voiceId}>
                  {draft.voiceName || draft.voiceId}
                </option>
              )}
          </Select>
        </Field>

        <Field>
          <Label>Or paste an ElevenLabs voice id</Label>
          <Input
            value={draft.voiceId}
            onChange={set("voiceId")}
            placeholder="21m00Tcm4TlvDq8ikWAM"
          />
        </Field>

        <ButtonRow>
          <Button
            size="sm"
            variant="secondary"
            onClick={handlePreviewVoice}
            disabled={isPreviewingVoice || !draft.voiceId || !elevenLabsApiKey}
          >
            {isPreviewingVoice ? "Generating..." : "Preview voice"}
          </Button>
        </ButtonRow>

        <Field>
          <Label>Master reference image</Label>
          <ButtonRow>
            <RefPreview $src={refPreview}>{!refPreview && "🎬"}</RefPreview>
            <Button
              size="sm"
              variant="secondary"
              onClick={handleGenerateReference}
              disabled={isGeneratingRef || !draft.visualDescription.trim()}
            >
              {isGeneratingRef ? "Generating..." : "Generate reference"}
            </Button>
          </ButtonRow>
        </Field>

        {status && <StatusMsg>{status}</StatusMsg>}
        {error && <StatusMsg $error>{error}</StatusMsg>}

        <ButtonRow>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : selected ? "Save changes" : "Create character"}
          </Button>
          {selected && (
            <>
              <Button size="sm" variant="secondary" onClick={handleArchiveToggle}>
                {selected.status === "archived" ? "Unarchive" : "Archive"}
              </Button>
              <Button size="sm" variant="secondary" onClick={handleDelete}>
                Delete
              </Button>
            </>
          )}
        </ButtonRow>
      </EditorPane>
    </Root>
  );
};

export default CharacterStudio;
